<template>
  <v-dialog
    v-model="openDialog"
    width="900"
    persistent
    @keydown.esc="openDialog = false"
  >
    <v-card block flat>
      <v-card-title class="title pb-0">
        <span class="title" v-if="questionnaireDialogOpened"
          >Request Additional Items</span
        >
        <span class="title" v-else-if="openItemQuestionnaire">Edit Draft</span>
        <span class="title" v-else>Create Custom Questionnaire</span>
        <v-spacer />
        <span class="caption error--text">*Required</span>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="info"
          class="text-xs-left mb-2"
        >
          Fill out the following fields to
          {{
            questionnaireDialogOpened
              ? 'request additional items'
              : 'create a custom questionnaire'
          }}. Do not include slashes (/) or periods (.) for document options.
          <span v-if="parentQuestionnaire && parentQuestionnaire.completed"
            >These items will be created as remaining items.</span
          >
          <span v-else-if="questionnaireDialogOpened"
            >These items will be added under "Additional Items" section in the
            questionnaire.</span
          >
          <span v-if="parentQuestionnaire && !parentQuestionnaire.is_draft">
            The client will receive a notification to complete these items.
          </span>
          <span v-else-if="parentQuestionnaire && parentQuestionnaire.is_draft">
            The client will not receive any notification because the
            questionnaire is in draft.
          </span>
        </v-alert>
        <v-layout wrap class="text-xs-left" v-if="!questionnaireDialogOpened">
          <v-flex
            xs6
            class="pr-2"
            :class="{ 'mb-2': !!openItemQuestionnaire }"
            v-if="multiple"
          >
            <div :class="{ 'grey--text': !!openItemQuestionnaire }">
              Send to<span class="error--text ml-1">*</span>
            </div>
            <v-autocomplete
              multiple
              class="default-input body-1"
              hide-details
              small-chips
              solo
              :items="entitiesWithClient"
              :disabled="loadingCompanies || !!openItemQuestionnaire"
              :loading="loadingCompanies"
              v-model="entityIds"
              placeholder="Select entities"
              item-text="name"
              item-value="id"
            >
              <template slot="selection" slot-scope="data">
                <v-chip small v-if="entityIds.indexOf(data.item.id) === 0">
                  <span :class="{ 'grey--text': !!openItemQuestionnaire }">{{
                    data.item.name
                  }}</span>
                </v-chip>
                <span
                  v-if="entityIds.indexOf(data.item.id) === 1"
                  class="grey--text caption"
                  >(+{{ entityIds.length - 1 }} other{{
                    entityIds.length > 2 ? 's' : ''
                  }})</span
                >
              </template>
            </v-autocomplete>
            <v-layout
              v-if="!loadingCompanies && !openItemQuestionnaire"
              align-start
              wrap
            >
              <span
                v-for="type in entityTypesWithoutAll"
                :key="type"
                class="primary--text caption mx-2 mb-1"
                style="cursor: pointer"
                @click="selectSpecificEntityType(type)"
                >{{ type }}{{ type === 'All' ? '' : 's' }}</span
              >
              <span
                v-if="entitiesWithClient.some(c => c.for_testing)"
                key="test"
                class="primary--text caption mx-2 mb-1"
                style="cursor: pointer"
                @click="selectSpecificEntityType('Test')"
                >Test Clients</span
              >
              <span
                v-if="!isEmpty(entityIds)"
                class="error--text caption mx-2 mb-1"
                style="cursor: pointer"
                @click="entityIds = []"
                >Clear</span
              >
            </v-layout>
          </v-flex>
          <v-flex xs4 :xs6="multiple" :class="multiple ? 'pl-2' : 'pr-2'">
            <div>Open items regarding</div>
            <v-text-field
              class="default-input body-1"
              hide-details
              solo
              v-model="openItemsName"
              placeholder="Enter a subject or topic"
            />
          </v-flex>
          <v-flex xs4 :xs6="multiple" :class="multiple ? 'pr-2' : 'px-2'">
            <div>
              Year<span class="error--text ml-1">*</span
              ><v-tooltip top
                ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                ><span
                  >Specify which folder documents should be organized into</span
                ></v-tooltip
              >
            </div>
            <v-autocomplete
              class="default-input body-1"
              solo
              :rules="rules.presence"
              :items="yearOptions"
              v-model="questionnaireYear"
              placeholder="Select a year"
            />
          </v-flex>
          <v-flex xs4 :xs6="multiple" class="pl-2">
            <div>
              Due Date<v-tooltip top
                ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                ><span
                  >Reminders will be sent out automatically to incomplete items
                  approaching the due date</span
                ></v-tooltip
              >
            </div>
            <v-text-field
              class="default-input body-1"
              type="date"
              solo
              item-text="name"
              item-value="id"
              :rules="rules.dueDate"
              v-model="questionnaireDueDate"
              placeholder="Choose a due date"
            />
          </v-flex>
        </v-layout>

        <v-alert
          v-if="showEmptyFormAlert"
          :value="true"
          outline
          :icon="$vuetify.breakpoint.smAndUp ? 'warning' : ''"
          color="error"
          class="text-xs-left mb-2"
        >
          Add a question or document
        </v-alert>
        <v-layout>
          Items
          <span class="error--text ml-1">*</span>
        </v-layout>
        <v-layout
          class="text-xs-left mb-3"
          align-center
          v-for="(question, index) in questionsToCreate"
          :key="index"
        >
          <span class="send-open__count">
            <sx-small>
              <b>{{ index + 1 }}.</b>
            </sx-small>
          </span>
          <sx-button-group :is-joined="true">
            <div @click="question.question_type = 'text'">
              <sx-button
                type="secondary"
                size="small"
                :is-left="true"
                :is-active="question.question_type === 'text'"
              >
                Question
              </sx-button>
            </div>
            <div @click="question.question_type = 'upload'">
              <sx-button
                type="secondary"
                size="small"
                :is-right="true"
                :is-active="question.question_type === 'upload'"
              >
                Upload
              </sx-button>
            </div>
          </sx-button-group>
          <v-layout v-if="question.question_type === 'upload'" align-center>
            <span class="mx-2 subheading">Please upload</span>
            <v-text-field
              v-model="question.text"
              solo
              hide-details
              class="default-input"
              placeholder="[DOCUMENT NAME]"
            />
          </v-layout>
          <v-layout v-else>
            <v-text-field
              v-model="question.text"
              solo
              hide-details
              class="default-input ml-2"
              placeholder="Please explain..."
            />
          </v-layout>
          <v-tooltip bottom class="ml-3" v-if="questionsToCreate.length !== 1">
            <i
              class="material-icons-outlined error--text"
              slot="activator"
              @click="questionsToCreate.splice(index, 1)"
              style="cursor: pointer"
              >delete</i
            >
            <span>Delete</span>
          </v-tooltip>
        </v-layout>
        <v-tooltip bottom>
          <v-btn
            slot="activator"
            icon
            class="default-input primary--text text-none"
            @click="addQuestionToCreate()"
          >
            <v-icon> add </v-icon>
          </v-btn>
          <span>Add Item</span>
        </v-tooltip>
      </v-card-text>
      <v-card-actions>
        <sx-flex :is-between="true">
          <!-- Disregard -->
          <sx-button-group>
            <div @click="deleteDraft()" v-if="openItemQuestionnaire">
              <sx-button
                type="error"
                :is-disabled="savingDraft || openItemsSending"
                :is-loading="removingDraft"
              >
                <span>Delete Draft</span>
                <sx-icon :size="16" name="trash" />
              </sx-button>
            </div>
          </sx-button-group>
          <sx-button-group x-align="right">
            <!-- Cancel -->
            <div @click="openDialog = false">
              <sx-button type="secondary">
                {{ $t('Cancel') }}
              </sx-button>
            </div>
            <!-- Save Draft -->
            <div v-if="!questionnaireDialogOpened" @click="sendOpenItems(true)">
              <sx-button
                type="secondary"
                :is-disabled="!formSavable || openItemsSending || removingDraft"
                :is-loading="savingDraft"
              >
                Save Draft
              </sx-button>
            </div>
            <!-- Send -->
            <div @click="sendOpenItems()">
              <sx-button
                type="primary"
                :is-disabled="!formSavable || savingDraft || removingDraft"
                :is-loading="openItemsSending"
              >
                {{
                  parentQuestionnaire && parentQuestionnaire.is_draft
                    ? 'Add'
                    : 'Send'
                }}
                {{ questionnaireDialogOpened ? 'Additional' : 'Open' }} Items
                <sx-icon
                  v-if="parentQuestionnaire && parentQuestionnaire.is_draft"
                  :size="16"
                  name="add"
                />
                <sx-icon v-else :size="16" name="send" />
              </sx-button>
            </div>
          </sx-button-group>
        </sx-flex>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { rules, yearOptions } from '@/constants/variables';
import SxFlex from '@/components/sx/SxFlex';
import SxIcon from '@/components/sx/SxIcon';
import SxSmall from '@/components/sx/SxSmall';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';

export default {
  name: 'SendOpenItemsDialog',
  components: {
    SxFlex,
    SxIcon,
    SxSmall,
    SxButton,
    SxButtonGroup,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    enableDraft: {
      type: Boolean,
      default: true,
    },
    openItemQuestionnaire: {
      type: Object,
      default: null,
    },
    parentQuestionnaire: {
      type: Object,
    },
    questionnaireDialogOpened: {
      type: Boolean,
      default: false,
    },
    selectedEntityIds: {
      type: Array,
      default: null,
      optional: true,
    },
    currentCompanyId: {
      type: Number,
    },
  },
  data() {
    return {
      rules,
      yearOptions,
      openItemsName: '',
      openDialog: false,
      showEmptyFormAlert: false,
      entityIds: [],
      questionnaireDueDate: moment()
        .add(1, 'weeks')
        .format('Y-MM-DD'),
      questionnaireYear: null,
      openItemsSending: false,
      removingDraft: false,
      questionsToCreate: [
        {
          text: '',
          section: 'Question',
          question_type: 'upload',
          options: [],
          newOption: '',
          advanced: false,
        },
      ],
      newOption: '',
      savingDraft: false,
      questionTypes: [
        {
          text: 'Upload',
          value: 'upload',
        },
        {
          text: 'Short Answer',
          value: 'text',
        },
      ],
    };
  },
  computed: {
    ...mapState('companies', ['loadingCompanies', 'companies']),
    ...mapGetters({
      currentUser: 'user',
    }),
    entitiesWithClient() {
      return this.companies
        .filter(
          c =>
            !this.currentUser.overview_setting.show_assigned_clients ||
            c.user.assigned_advisor_id === this.currentUser.id,
        )
        .map(entity => ({
          id: entity.id,
          name: `${this.getDisplayName(entity)}${
            entity.user.for_testing ? ' (Test)' : ''
          }`,
          entity_type: entity.entity_type,
          for_testing: entity.user.for_testing,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    entityTypesWithoutAll() {
      return [...new Set(this.companies.map(c => c.entity_type))];
    },
    formSavable() {
      return (
        !isEmpty(this.entityIds) &&
        this.questionnaireYear &&
        (!this.questionnaireDueDate ||
          moment() < moment(this.questionnaireDueDate))
      );
    },
    questionsToSave() {
      return this.questionsToCreate.filter(d => !isEmpty(d.text));
    },
    parentQuestionnaireInDraft() {
      return this.parentQuestionnaire && this.parentQuestionnaire.is_draft;
    },
  },
  methods: {
    isEmpty,
    moment,
    addQuestionToCreate() {
      this.questionsToCreate.push({
        text: '',
        section: 'Question',
        question_type: 'upload',
        options: [],
        advanced: false,
      });
    },
    getDisplayName(entity) {
      if (this.isIndividual(entity)) {
        return entity.tax_return_name;
      } else {
        return `${entity.name} (${entity.user.full_name})`;
      }
    },
    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
    selectSpecificEntityType(type) {
      if (type === 'All') {
        this.entityIds = this.entitiesWithClient.map(e => e.id);
      } else if (type === 'Test') {
        this.entityIds = this.entitiesWithClient
          .filter(e => e.for_testing)
          .map(e => e.id);
      } else {
        this.entityIds = this.entitiesWithClient
          .filter(e => e.entity_type === type)
          .map(e => e.id);
      }
    },
    reset() {
      if (this.questionnaireDialogOpened) {
        this.entityIds = [this.parentQuestionnaire.company_id];
        this.openItemsName = this.parentQuestionnaire.name;
        this.questionnaireYear = this.parentQuestionnaire.tax_year;
      } else {
        if (!isEmpty(this.selectedEntityIds)) {
          this.entityIds = this.selectedEntityIds;
        } else {
          if (this.multiple) {
            this.entityIds = [];
          } else {
            this.entityIds = [this.currentCompanyId];
          }
        }
        this.parentQuestionnaire = null;
        this.openItemsName = '';
        this.questionnaireYear = null;
      }
      this.questionnaireDueDate = moment()
        .add(1, 'weeks')
        .format('Y-MM-DD');
      this.questionsToCreate = [
        {
          text: '',
          section: 'Question',
          question_type: 'upload',
          options: [],
          advanced: false,
        },
      ];
    },
    async deleteDraft() {
      try {
        this.removingDraft = true;
        await API.questionnaire.deleteQuestionnaire({
          companyId: this.openItemQuestionnaire.company_id,
          id: this.openItemQuestionnaire.id,
        });
        this.openDialog = false;
        this.$emit('refresh-questionnaires');
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Draft successfully removed.',
        });
      } catch (e) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Error removing draft.',
        });
      } finally {
        this.removingDraft = false;
      }
    },
    async sendOpenItems(saveAsDraft) {
      if (
        !this.formSavable ||
        this.removingDraft ||
        this.savingDraft ||
        this.openItemsSending
      )
        return;

      if (!this.questionsToSave.length) {
        this.showEmptyFormAlert = true;
        return;
      }

      if (saveAsDraft) {
        this.savingDraft = true;
      } else {
        this.openItemsSending = true;
      }

      this.showEmptyFormAlert = false;
      try {
        const questionsToCreate = this.questionsToSave.map(d => ({
          text:
            d.question_type === 'upload' ? `Please upload ${d.text}` : d.text,
          options: d.question_type === 'upload' ? [d.text] : [],
          question_type: d.question_type,
        }));
        const response1 = await API.questionnaire.sendOpenItems({
          companyIds: this.entityIds,
          taxYear: this.questionnaireYear,
          dueDate: this.questionnaireDueDate,
          isDraft: !!saveAsDraft,
          questionsToCreate,
          name: this.openItemsName,
          relevantQuestionnaireId: this.questionnaireDialogOpened
            ? this.parentQuestionnaire.id
            : null,
          openItemId: this.openItemQuestionnaire
            ? this.openItemQuestionnaire.id
            : null,
        });
        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              saveAsDraft || this.parentQuestionnaireInDraft
                ? 'Draft successfully saved.'
                : "You've successfully sent the custom questionnaire.",
          });
          this.openDialog = false;
          this.$emit('refresh-questionnaires');
          if (this.questionnaireDialogOpened) {
            this.$emit(
              'refresh-questionnaire-dialog',
              this.parentQuestionnaire,
            );
          }
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message:
            saveAsDraft || this.parentQuestionnaireInDraft
              ? 'There was a problem saving the draft'
              : 'There was a problem sending the custom questionnaire.',
        });
      } finally {
        this.openItemsSending = false;
        this.savingDraft = false;
      }
    },
    async getAllQuestionnaireTemplates() {
      const response = await API.questionnaireTemplate.getAllQuestionnaireTemplatesInfo();
      this.questionnaireTemplates = response.data;
    },
    async loadQuestions(questionnaire) {
      const response = await API.questionnaire.getQuestionnaire({
        companyId: questionnaire.company_id,
        id: questionnaire.id,
      });

      this.questionsToCreate = response.data.questionnaire_questions.map(q => {
        let question = Object.assign({}, q);

        if (q.question_type === 'upload') {
          question.text = question.text.replace('Please upload ', '');
        }
        return question;
      });

      this.entityIds = [this.parentQuestionnaire.company_id];
      this.openItemsName = this.parentQuestionnaire.name;
      this.questionnaireYear = this.parentQuestionnaire.tax_year;

      this.questionnaireDueDate = moment()
        .add(1, 'weeks')
        .format('Y-MM-DD');
    },
  },
  created() {
    this.openDialog = this.value;
  },
  watch: {
    value(newVal) {
      this.openDialog = newVal;
      this.showEmptyFormAlert = false;
      if (this.openDialog) {
        if (this.openItemQuestionnaire) {
          this.loadQuestions(this.openItemQuestionnaire);
        } else {
          this.reset();
          this.getAllQuestionnaireTemplates();
        }
      }
    },
    openDialog(newVal) {
      this.$emit('input', newVal);
    },
  },
};
</script>

<style scoped="true">
.send-open__count {
  align-items: center;
  display: flex;
  height: 32px;
  justify-content: center;
  margin-right: 12px;
  min-width: 32px;
  width: 32px;

  background-color: var(--gray-100);
  border-radius: 100%;

  font-weight: bold;
}
</style>
